import type {
  ComponentProps,
  ChangeEventHandler,
  FocusEventHandler,
} from 'react'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import type {
  BaseAutocompleteProps,
} from './BaseAutocompleteInput'
import BaseAutocompleteInput from './BaseAutocompleteInput'
import NoResultsCopy from '../Search/NoResultsCopy'
import type Input from '../TripModal/TripItems/Common/Fields/RHFInput'

import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import useAirportTypeahead from 'hooks/GlobalSearch/useAirportTypeahead'
import { useAirport } from 'tripPlanner/hooks/api/flights'

interface Props extends ComponentProps<typeof Input> {
  label?: string
  name: string
  onAirportSelect: (airport: App.AirportLocation) => void
  placeholder?: string
  noParentAirports?: boolean
}

const CHARACTER_THRESHOLD = 1
const DEBOUNCE_DELAY = 300
const MAX_RESULTS = 5

function AirportAutocomplete({
  name,
  onFocus,
  onAirportSelect,
  placeholder,
  label,
  noParentAirports,
  required,
}: Props) {
  const intl = useIntl()

  const noResultsMessage = intl.formatMessage({
    id: 'S-C8b716eR4nSUU63yKa',
    defaultMessage: 'Try searching for a different airport.',
    description: 'No results message shown when airport search yields no matches in trip planner',
  })

  const [searchPhrase, setSearchPhrase] = useState('')

  const presentationSelector = useMemo<BaseAutocompleteProps<App.AirportLocation>['presentationSelector']>(() =>
    (option) => ({
      title: option.airportName,
      subtitle: intl.formatMessage(
        {
          id: 'XGEdiq_PXlbj6fa54ZdV',
          defaultMessage: '{cityDesc} ({airportCode})',
          description: 'Airport city description with code in trip planner airport autocomplete dropdown',
        },
        { cityDesc: option.cityDesc, airportCode: option.airportCode },
      ),
      startIcon: <LinePlaneFlyIcon colour="normal-contrast" />,
    }), [intl])

  const initValueFormat = useCallback((airport?: App.AirportLocation) =>
    airport
      ? intl.formatMessage(
          {
            id: 'fKuEP7EPIr9ot_zxrZQs',
            defaultMessage: '{airportName} ({airportCode})',
            description: 'Airport name with code in trip planner airport autocomplete initial value',
          },
          { airportName: airport.airportName, airportCode: airport.airportCode },
        )
      : '', [intl])

  const { setValue, control } = useFormContext()
  const {
    field: { ref, value },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      required: intl.formatMessage({
        id: 'hbUedtL8b46ch25Zgi7z',
        defaultMessage: 'Please select an airport from your search.',
        description: 'Validation error when no airport is selected in trip planner flight form',
      }),
    },
  })

  const {
    hasResults,
    isLoading: predictionsLoading,
    results,
  } = useAirportTypeahead(searchPhrase, {
    noParentAirports,
    debounceDelay: DEBOUNCE_DELAY,
    characterThreshold: CHARACTER_THRESHOLD,
    maxResults: MAX_RESULTS,
  })

  const airportQuery = useAirport({
    code: value,
    onSuccess: (airport) => {
      if (airport) {
        setSearchPhrase(initValueFormat(airport))
        setValue(name, airport.airportCode)
      } else {
        setValue(name, undefined)
      }
    },
    onError: () => {
      setValue(name, undefined)
    },
  })

  useEffect(() => {
    // Attempts to set airport on mount if data exists
    if (airportQuery.data) {
      setSearchPhrase(initValueFormat(airportQuery.data))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSelect = useCallback(
    (airport: App.AirportLocation) => {
      setValue(name, airport.airportCode, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setSearchPhrase(initValueFormat(airport))
      onAirportSelect?.(airport)
    },
    [name, setValue, onAirportSelect, initValueFormat],
  )

  const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setSearchPhrase('')
      setValue(name, undefined)
      onFocus?.(e)
    },
    [onFocus, setValue, name],
  )

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setSearchPhrase(e.currentTarget.value)
    },
    [],
  )

  return (
    <BaseAutocompleteInput
      ref={ref}
      required={required}
      value={searchPhrase}
      error={error?.message}
      label={label}
      placeholder={placeholder}
      canRenderDropdown={searchPhrase.length > CHARACTER_THRESHOLD}
      canRenderNoResult={!hasResults && !predictionsLoading}
      name={name}
      noResultComponent={
        <NoResultsCopy
          message={noResultsMessage}
          searchPhrase={searchPhrase}
        />
      }
      startIcon={<LineMapMarkerIcon />}
      onChange={handleChange}
      onFocus={handleFocus}
      onOptionSelect={handleSelect}
      options={results}
      presentationSelector={presentationSelector}
      isLoading={predictionsLoading}
    />
  )
}

export default AirportAutocomplete
